import { writable, derived, get } from 'svelte/store';
import { user } from './authStore.js';
import { splitCountryValue } from '../lib/canadaTax.js';
import { normalizeDueTerms } from '../lib/dueTerms.js';
import {
  getInitialFormData,
  getInitialBillSettings,
  getInitialInvoiceTrackingSettings
} from '../lib/settings/constants.js';
import {
  buildIntegrationsPayload,
  postIntegrations,
  fetchIntegrations
} from '../lib/settings/integrations.js';
import { isXeroReauthReason } from '../lib/xeroReauth.js';
import { uiSlotsFromSettings, legacyFromSlot1, validateSlots, slotReady } from '../lib/trackingSlots.js';

function createSettingsStore() {
  const loading = writable(false);
  const error = writable(null);
  const success = writable(null);
  const initialized = writable(false);

  const subscriptionData = writable(null);
  const subscriptionLoading = writable(true);
  const subscriptionError = writable(null);
  const subscriptionMessage = writable(null);
  const selectedPlan = writable(null);
  const subscribing = writable(false);

  const formData = writable(getInitialFormData());
  const xeroTaxRates = writable([]);
  const loadingTaxRates = writable(false);

  // True when the current user's XERO settings are slaved to the org owner
  // (a member following the owner's Xero org) and shown read-only. A member on
  // their own Xero org self-manages these. Set from GET /integrations.
  // `managedByOwner` is kept as an alias of the Xero flag for back-compat.
  const managedByOwner = writable(false);
  const xeroManagedByOwner = writable(false);
  // True for any non-owner member: JobTread config is org-shared (read-only)
  // regardless of which Xero org the member connects to.
  const jobtreadManagedByOwner = writable(false);

  const jobtreadConnectionStatus = writable(null);
  const jobtreadOrgName = writable('');
  const availableOrganizations = writable([]);
  const fetchingOrganizations = writable(false);
  const showManualOrgId = writable(false);

  const autoSyncOnLoginLocal = writable(false);
  const savingAutoSync = writable(false);

  const assignmentLensDefault = writable('costGroup');
  const savingAssignmentLens = writable(false);

  const seeTeamUploads = writable(false);
  const savingSeeTeamUploads = writable(false);

  const billSettings = writable(getInitialBillSettings());
  const xeroBankAccounts = writable([]);
  const loadingBankAccounts = writable(false);

  const vendorAliasesList = writable([]);
  const loadingVendorAliases = writable(false);
  const vendorAliasSuccess = writable(null);

  const invoiceTrackingSettings = writable(getInitialInvoiceTrackingSettings());

  // Cost-and-Fee (cost-plus) invoice settings
  const invoiceCostFeeSettings = writable({ enabled: false, feeAccountCode: '', feeLabel: "Builder's Fee" });

  const xeroConnected = writable(false);
  const xeroNeedsFix = writable(false);
  const checkingXeroStatus = writable(true);
  const showDisconnectConfirm = writable(false);
  const disconnecting = writable(false);
  const jobtreadConnected = writable(false);

  const isQuickBooks = derived(formData, ($formData) => $formData.accountingPlatform === 'quickbooks');

  // QuickBooks users run JobTread-only — Xero invoice/expense codes are not required.
  const formValid = derived(formData, ($formData) =>
    $formData.country.trim() !== '' &&
    ($formData.accountingPlatform === 'quickbooks' ||
      ($formData.xeroInvoiceCode.trim() !== '' && $formData.xeroExpenseCode.trim() !== ''))
  );

  const trackingCategories = writable([]);
  const loadingTrackingCategories = writable(false);
  const trackingCategoriesError = writable(null);

  const billSettingsValid = derived(billSettings, ($billSettings) =>
    ($billSettings.tracking || []).filter((slot) => slot.enabled).every(slotReady)
  );

  const invoiceTrackingValid = derived(invoiceTrackingSettings, ($invoiceTrackingSettings) =>
    ($invoiceTrackingSettings.tracking || []).filter((slot) => slot.enabled).every(slotReady)
  );

  async function fetchXeroTaxRates() {
    loadingTaxRates.set(true);
    try {
      const response = await fetch('/api/xero/tax-rates');
      if (response.ok) {
        const data = await response.json();
        xeroTaxRates.set(data.taxRates || []);
      } else {
        xeroTaxRates.set([]);
      }
    } catch (err) {
      console.error('Error fetching Xero tax rates:', err);
      xeroTaxRates.set([]);
    } finally {
      loadingTaxRates.set(false);
    }
  }

  async function loadSettings() {
    loading.set(true);
    error.set(null);

    try {
      const data = await fetchIntegrations();
      managedByOwner.set(!!data.xeroManagedByOwner);
      xeroManagedByOwner.set(!!data.xeroManagedByOwner);
      jobtreadManagedByOwner.set(!!data.jobtreadManagedByOwner);
      const jobtreadApiKey = data.integrations.jobtread.apiKey === 'BLANK' ? '' : data.integrations.jobtread.apiKey;
      const jobtreadOrgId = data.integrations.jobtread.orgId === 'BLANK' ? '' : data.integrations.jobtread.orgId;

      formData.set({
        jobtreadApiKey,
        jobtreadOrgId,
        xeroInvoiceCode: data.integrations.xero.invoiceCode,
        xeroExpenseCode: data.integrations.xero.expenseCode,
        xeroUSTaxType: data.integrations.xero.usTaxType || '',
        xeroGBTaxType: data.integrations.xero.gbTaxType || '',
        xeroCATaxType: data.integrations.xero.caTaxType || '',
        country: data.country,
        province: data.province || null,
        excludedCostCodes: data.excludedCostCodes || '',
        accountingPlatform: data.accountingPlatform === 'quickbooks' ? 'quickbooks' : 'xero'
      });

      const loadedBillSlots = uiSlotsFromSettings(data.billSettings, 'jtBillNameField');
      const loadedBillLegacy = legacyFromSlot1(loadedBillSlots, 'jtBillNameField');
      billSettings.set({
        jtBillNameField: loadedBillLegacy.jtBillNameField,
        xeroCategoryName: loadedBillLegacy.xeroCategoryName,
        xeroCategoryID: loadedBillLegacy.xeroCategoryID,
        enabled: loadedBillLegacy.enabled,
        tracking: loadedBillSlots,
        billmodedefaultjob: data.billSettings?.billmodedefaultjob || false,
        billsAndExpenses: data.billSettings?.billsAndExpenses ?? false,
        xeroExpenseBankAccountID: data.billSettings?.xeroExpenseBankAccountID || '',
        xeroExpenseBankAccountName: data.billSettings?.xeroExpenseBankAccountName || '',
        xeroExpenseAutoPayment: data.billSettings?.xeroExpenseAutoPayment ?? true,
        xeroExpenseSyncMode: data.billSettings?.xeroExpenseSyncMode || 'bill',
        skipAutoApprove: data.billSettings?.skipAutoApprove ?? false,
        markDirectImportBillsAsPaid: data.billSettings?.markDirectImportBillsAsPaid ?? false,
        requireDirectBillApproval: data.billSettings?.requireDirectBillApproval ?? false,
        directBillDueTerms: normalizeDueTerms(data.billSettings?.directBillDueTerms),
        multiCurrencyEnabled: data.billSettings?.multiCurrencyEnabled ?? false
      });

      const loadedInvoiceSlots = uiSlotsFromSettings(data.invoiceTrackingSettings, 'jtJobField');
      const loadedInvoiceLegacy = legacyFromSlot1(loadedInvoiceSlots, 'jtJobField');
      invoiceTrackingSettings.set({
        jtJobField: loadedInvoiceLegacy.jtJobField,
        xeroCategoryName: loadedInvoiceLegacy.xeroCategoryName,
        xeroCategoryID: loadedInvoiceLegacy.xeroCategoryID,
        enabled: loadedInvoiceLegacy.enabled,
        tracking: loadedInvoiceSlots,
        lineItemRoundingMode: data.invoiceTrackingSettings?.lineItemRoundingMode === 'summarise' ? 'summarise' : 'preserve',
        syncAsDraft: data.invoiceTrackingSettings?.syncAsDraft ?? false
      });

      invoiceCostFeeSettings.set({
        enabled: data.invoiceCostFeeSettings?.enabled || false,
        feeAccountCode: data.invoiceCostFeeSettings?.feeAccountCode || '',
        feeLabel: data.invoiceCostFeeSettings?.feeLabel || "Builder's Fee"
      });

      autoSyncOnLoginLocal.set(data.autoSyncOnLogin || false);
      assignmentLensDefault.set(data.assignmentLensDefault === 'costCode' ? 'costCode' : 'costGroup');
      seeTeamUploads.set(data.seeTeamUploads === true);

      if (data.country === 'US' || data.country === 'GB' || data.country === 'GI' || data.country === 'CA') {
        await fetchXeroTaxRates();
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      error.set('Failed to load settings. Please try again.');
    } finally {
      loading.set(false);
    }
  }

  async function checkXeroConnection() {
    try {
      checkingXeroStatus.set(true);
      const response = await fetch('/api/xero/check-auth');
      const data = await response.json();
      if (response.ok && data.authenticated === true) {
        xeroConnected.set(true);
        xeroNeedsFix.set(false);
      } else if (isXeroReauthReason(data.reason)) {
        // Broken link to a pinned org. Stay on Settings so the user can
        // disconnect. OAuth happens later, when they open a Xero function.
        xeroConnected.set(false);
        xeroNeedsFix.set(true);
      } else {
        xeroConnected.set(false);
        xeroNeedsFix.set(false);
      }
    } catch {
      xeroConnected.set(false);
      xeroNeedsFix.set(false);
    } finally {
      checkingXeroStatus.set(false);
    }
  }

  async function checkJobTreadAuthStatus() {
    try {
      const response = await fetch('/api/jt/check-auth');
      const data = await response.json();
      jobtreadConnected.set(response.ok && data.authenticated === true);
    } catch {
      jobtreadConnected.set(false);
    }
  }

  // Settings stay in memory across client-side logins. Reloading only when the
  // signed-in user changes keeps an admin's read-only lock from sticking after
  // the owner signs in.
  let loadedForUserId = null;

  function resetSettings() {
    loadedForUserId = null;
    initialized.set(false);
    managedByOwner.set(false);
    xeroManagedByOwner.set(false);
    jobtreadManagedByOwner.set(false);
    formData.set(getInitialFormData());
    billSettings.set(getInitialBillSettings());
  }

  async function initializeSettings() {
    const currentUserId = get(user)?.id ?? null;
    if (get(initialized) && loadedForUserId != null && String(loadedForUserId) === String(currentUserId)) {
      return;
    }
    if (loadedForUserId != null && String(loadedForUserId) !== String(currentUserId)) {
      managedByOwner.set(false);
      xeroManagedByOwner.set(false);
      jobtreadManagedByOwner.set(false);
    }

    await Promise.all([loadSettings(), checkJobTreadAuthStatus(), loadSubscriptionStatus()]);

    const $formData = get(formData);
    const isQb = $formData.accountingPlatform === 'quickbooks';

    // Check Xero whenever JobTread is connected — do not require saved account
    // codes first (disconnect clears those and blocked reconnect otherwise).
    if (get(jobtreadConnected) && !isQb) {
      await checkXeroConnection();
    } else {
      checkingXeroStatus.set(false);
      xeroConnected.set(false);
      xeroNeedsFix.set(false);
    }

    loadedForUserId = get(user)?.id ?? currentUserId;
    initialized.set(true);
  }

  async function disconnectXero() {
    disconnecting.set(true);
    error.set(null);
    try {
      const response = await fetch('/api/xero/disconnect', { method: 'POST' });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to disconnect');
      }
      showDisconnectConfirm.set(false);
      xeroConnected.set(false);
      xeroNeedsFix.set(false);
      success.set('Xero has been disconnected. You can reconnect at any time from the dashboard.');
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      console.error('Xero disconnect failed:', err);
      error.set(err.message || 'Failed to disconnect from Xero');
    } finally {
      disconnecting.set(false);
    }
  }

  async function loadSubscriptionStatus() {
    subscriptionLoading.set(true);
    subscriptionError.set(null);
    try {
      const response = await fetch('/api/stripe/subscription-status');
      if (response.ok) {
        const data = await response.json();
        subscriptionData.set(data.subscription);
      } else {
        subscriptionError.set('Failed to load subscription status');
      }
    } catch {
      subscriptionError.set('Failed to load subscription status');
    } finally {
      subscriptionLoading.set(false);
    }
  }

  async function handleSubscribe() {
    const plan = get(selectedPlan);
    if (!plan) return;
    subscribing.set(true);
    subscriptionError.set(null);
    try {
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        subscriptionError.set(data.error || 'Failed to start checkout');
      }
    } catch {
      subscriptionError.set('Failed to start checkout. Please try again.');
    } finally {
      subscribing.set(false);
    }
  }

  async function openCustomerPortal() {
    try {
      const response = await fetch('/api/stripe/customer-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        subscriptionError.set(data.error || 'Failed to open billing portal');
      }
    } catch {
      subscriptionError.set('Failed to open billing portal');
    }
  }

  async function handleCountryChange(event) {
    // Canada is picked per province ('CA-BC'); store country + province separately.
    const { country, province } = splitCountryValue(event.target.value);
    formData.update((current) => ({ ...current, country, province }));

    if (country === 'US' || country === 'GB' || country === 'GI' || country === 'CA') {
      await fetchXeroTaxRates();
    } else {
      formData.update((current) => ({
        ...current,
        xeroUSTaxType: '',
        xeroGBTaxType: '',
        xeroCATaxType: ''
      }));
      xeroTaxRates.set([]);
    }
  }

  async function fetchOrganizations() {
    const $formData = get(formData);
    if (!$formData.jobtreadApiKey.trim()) {
      error.set('Please enter JobTread API Key first.');
      return;
    }

    fetchingOrganizations.set(true);
    error.set(null);
    availableOrganizations.set([]);

    try {
      const response = await fetch('/api/users/fetch-jobtread-organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: $formData.jobtreadApiKey })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        availableOrganizations.set(data.organizations);

        if (data.organizations.length === 1) {
          formData.update((current) => ({ ...current, jobtreadOrgId: data.organizations[0].id }));
          jobtreadOrgName.set(data.organizations[0].name);
        } else if (data.organizations.length === 0) {
          error.set('No organizations found for this API key.');
          showManualOrgId.set(true);
        }
      } else {
        error.set(data.error || 'Failed to fetch organizations');
        showManualOrgId.set(true);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      error.set('Failed to fetch organizations. You can enter the Organisation ID manually.');
      showManualOrgId.set(true);
    } finally {
      fetchingOrganizations.set(false);
    }
  }

  async function checkJobTreadConnection() {
    const $formData = get(formData);
    if (!$formData.jobtreadApiKey.trim() || !$formData.jobtreadOrgId.trim()) {
      error.set('Please enter JobTread API Key and Organisation ID first.');
      return;
    }

    jobtreadConnectionStatus.set('checking');
    error.set(null);

    try {
      const response = await fetch('/api/users/validate-jobtread-self', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey: $formData.jobtreadApiKey,
          orgId: $formData.jobtreadOrgId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        jobtreadConnectionStatus.set('success');
        jobtreadOrgName.set(data.organization.name);
        await saveSettingsQuietly();
      } else {
        jobtreadConnectionStatus.set('error');
        error.set(data.error || 'Failed to connect to JobTread');
      }
    } catch (err) {
      console.error('JobTread connection check failed:', err);
      jobtreadConnectionStatus.set('error');
      error.set('Failed to check JobTread connection');
    }
  }

  async function performSave() {
    const payload = buildIntegrationsPayload(get(formData), get(billSettings), get(invoiceTrackingSettings), get(invoiceCostFeeSettings));
    return postIntegrations(payload);
  }

  async function saveSettingsQuietly() {
    if (!get(formValid)) return;

    try {
      await performSave();
      console.log('Settings saved automatically after JobTread connection test');
    } catch (err) {
      console.error('Quiet settings save failed:', err);
    }
  }

  async function loadTrackingCategories() {
    if (get(trackingCategories).length) return get(trackingCategories);
    loadingTrackingCategories.set(true);
    trackingCategoriesError.set(null);
    try {
      const response = await fetch('/api/xero/tracking-categories');
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to fetch tracking categories from Xero');
      }
      const categories = data.categories || [];
      trackingCategories.set(categories);
      return categories;
    } catch (err) {
      trackingCategoriesError.set(err.message || 'Failed to fetch tracking categories from Xero');
      throw err;
    } finally {
      loadingTrackingCategories.set(false);
    }
  }

  // Confirm each enabled slot still points at an active Xero category and
  // copy the canonical name and id onto the slot.
  async function resolveEnabledSlots(slots) {
    const enabled = slots.filter((slot) => slot.enabled);
    if (!enabled.length) return slots;
    const categories = await loadTrackingCategories();
    return slots.map((slot) => {
      if (!slot.enabled) return slot;
      const match = categories.find((category) => category.trackingCategoryID === slot.xeroCategoryID)
        || categories.find((category) => category.name === slot.xeroCategoryName);
      if (!match || (match.status && match.status !== 'ACTIVE')) {
        throw new Error(`Tracking category "${slot.xeroCategoryName || slot.xeroCategoryID}" is not an active category in Xero.`);
      }
      return { ...slot, xeroCategoryID: match.trackingCategoryID, xeroCategoryName: match.name };
    });
  }

  function disableTrackingSlots(settings) {
    const tracking = (settings.tracking || []).map((slot) => ({ ...slot, enabled: false }));
    return { ...settings, ...legacyFromSlot1(tracking, 'jtBillNameField'), tracking, enabled: false };
  }

  async function saveAutoSyncOnLogin(value) {
    savingAutoSync.set(true);
    try {
      const response = await fetch('/api/users/auto-sync-on-login', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoSyncOnLogin: value })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save preference');
      }
      autoSyncOnLoginLocal.set(value);
      success.set('Auto-sync preference updated!');
      setTimeout(() => success.set(null), 3000);
    } catch (err) {
      console.error('Error saving auto-sync on login:', err);
      error.set('Failed to save auto-sync preference.');
      autoSyncOnLoginLocal.set(!value);
    } finally {
      savingAutoSync.set(false);
    }
  }

  async function saveSeeTeamUploads(value) {
    const next = !!value;
    const previous = get(seeTeamUploads);
    if (previous === next) return;
    seeTeamUploads.set(next);
    savingSeeTeamUploads.set(true);
    try {
      const response = await fetch('/api/users/see-team-uploads', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seeTeamUploads: !!value })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save preference');
      }
      success.set('Team uploads setting updated!');
      setTimeout(() => success.set(null), 2000);
    } catch (err) {
      console.error('Error saving see team uploads:', err);
      error.set('Failed to save team uploads setting.');
      seeTeamUploads.set(previous);
    } finally {
      savingSeeTeamUploads.set(false);
    }
  }

  async function saveAssignmentLensDefault(value) {
    if (value !== 'costGroup' && value !== 'costCode') return;
    const previous = get(assignmentLensDefault);
    assignmentLensDefault.set(value);
    savingAssignmentLens.set(true);
    try {
      const response = await fetch('/api/users/assignment-lens-default', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignmentLensDefault: value })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save preference');
      }
      success.set('Bill assignment default updated!');
      setTimeout(() => success.set(null), 3000);
    } catch (err) {
      console.error('Error saving assignment lens default:', err);
      error.set('Failed to save bill assignment default.');
      assignmentLensDefault.set(previous);
    } finally {
      savingAssignmentLens.set(false);
    }
  }

  async function saveBillSettings() {
    const current = get(billSettings);
    const problem = validateSlots(current.tracking);
    if (problem) {
      error.set(problem);
      billSettings.set(disableTrackingSlots(current));
      setTimeout(() => error.set(null), 5000);
      return;
    }

    try {
      const tracking = await resolveEnabledSlots(uiSlotsFromSettings(current, 'jtBillNameField'));
      const next = { ...current, ...legacyFromSlot1(tracking, 'jtBillNameField'), tracking };
      billSettings.set(next);
      await postIntegrations(buildIntegrationsPayload(get(formData), next, get(invoiceTrackingSettings)));
      success.set('✅ Bill tracking settings saved.');
      setTimeout(() => success.set(null), 3000);
    } catch (err) {
      console.error('Bill settings save failed:', err);
      error.set(err.message || 'Failed to save bill settings');
      billSettings.set(disableTrackingSlots(get(billSettings)));
      setTimeout(() => error.set(null), 5000);
    }
  }

  async function saveInvoiceTrackingSettings() {
    const current = get(invoiceTrackingSettings);
    const problem = validateSlots(current.tracking);
    if (problem) {
      error.set('[INVOICE] ' + problem);
      const tracking = (current.tracking || []).map((slot) => ({ ...slot, enabled: false }));
      invoiceTrackingSettings.set({ ...current, ...legacyFromSlot1(tracking, 'jtJobField'), tracking });
      setTimeout(() => error.set(null), 5000);
      return;
    }

    try {
      const tracking = await resolveEnabledSlots(uiSlotsFromSettings(current, 'jtJobField'));
      const next = { ...current, ...legacyFromSlot1(tracking, 'jtJobField'), tracking };
      invoiceTrackingSettings.set(next);
      await postIntegrations(buildIntegrationsPayload(get(formData), get(billSettings), next));
      success.set('✅ Invoice tracking settings saved.');
      setTimeout(() => success.set(null), 3000);
    } catch (err) {
      console.error('Invoice tracking settings save failed:', err);
      error.set('[INVOICE] ' + (err.message || 'Failed to save invoice tracking settings'));
      const tracking = (get(invoiceTrackingSettings).tracking || []).map((slot) => ({ ...slot, enabled: false }));
      invoiceTrackingSettings.update((settings) => ({ ...settings, ...legacyFromSlot1(tracking, 'jtJobField'), tracking }));
      setTimeout(() => error.set(null), 5000);
    }
  }

  async function fetchXeroBankAccounts() {
    loadingBankAccounts.set(true);
    try {
      const response = await fetch('/api/xero/bank-accounts');
      if (response.ok) {
        const data = await response.json();
        const accounts = data.accounts || [];
        xeroBankAccounts.set(accounts);
        const $billSettings = get(billSettings);
        if ($billSettings.xeroExpenseBankAccountID) {
          const match = accounts.find((a) => a.accountID === $billSettings.xeroExpenseBankAccountID);
          if (match) {
            billSettings.update((current) => ({ ...current, xeroExpenseBankAccountName: match.name }));
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch Xero bank accounts:', err);
    } finally {
      loadingBankAccounts.set(false);
    }
  }

  async function fetchVendorAliases() {
    loadingVendorAliases.set(true);
    try {
      const response = await fetch('/api/users/vendor-aliases');
      if (response.ok) {
        const data = await response.json();
        const aliases = data.vendorAliases || {};
        vendorAliasesList.set(
          Object.entries(aliases).map(([key, vendorId]) => ({
            sourceName: key,
            jtVendorId: vendorId
          }))
        );
      }
    } catch (err) {
      console.error('Failed to fetch vendor aliases:', err);
    } finally {
      loadingVendorAliases.set(false);
    }
  }

  async function deleteVendorAlias(sourceName) {
    try {
      const response = await fetch('/api/users/vendor-aliases', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceName })
      });
      if (response.ok) {
        vendorAliasesList.update((list) => list.filter((a) => a.sourceName !== sourceName));
        vendorAliasSuccess.set('Alias deleted');
        setTimeout(() => vendorAliasSuccess.set(null), 2000);
      }
    } catch (err) {
      console.error('Failed to delete vendor alias:', err);
    }
  }

  async function saveSettings() {
    if (!get(formValid)) {
      error.set('Please fill in all required fields.');
      return;
    }

    loading.set(true);
    error.set(null);
    success.set(null);

    let invoiceTrackingError = null;

    try {
      // Best-effort auto-enable of invoice tracking on save: if both fields are
      // filled but the toggle is off, try to validate the Xero category and turn
      // it on. If the category can't be verified we still persist the typed values
      // (left disabled) and surface the same validation message as the manual
      // toggle, rather than failing the whole save.
      const invoiceSlots = uiSlotsFromSettings(get(invoiceTrackingSettings), 'jtJobField');
      if (slotReady(invoiceSlots[0]) && !invoiceSlots[0].enabled) {
        try {
          const tracking = await resolveEnabledSlots(invoiceSlots.map((slot, index) => (
            index === 0 ? { ...slot, enabled: true } : slot
          )));
          invoiceTrackingSettings.update((current) => ({
            ...current,
            ...legacyFromSlot1(tracking, 'jtJobField'),
            tracking
          }));
        } catch (trackingErr) {
          console.error('Invoice tracking auto-enable failed:', trackingErr);
          invoiceTrackingError = '[INVOICE] ' + (trackingErr.message || 'Failed to save invoice tracking settings');
        }
      }

      await performSave();

      if (invoiceTrackingError) {
        // Everything else saved; keep the page so the validation message is visible.
        error.set(invoiceTrackingError);
        setTimeout(() => error.set(null), 5000);
      } else {
        success.set('✅ Settings saved successfully!');
        jobtreadConnectionStatus.set(null);
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (err) {
      console.error('Settings save failed:', err);
      error.set(err.message || 'Failed to save settings');
    } finally {
      loading.set(false);
    }
  }

  /**
   * Save settings without reloading — used when leaving Xero settings for the
   * cost-code mappings page so unsaved edits (and CoA validation) run first.
   * @returns {Promise<boolean>} true if save succeeded
   */
  async function saveSettingsForNavigation() {
    if (!get(formValid)) {
      error.set('Please fill in all required fields.');
      return false;
    }

    loading.set(true);
    error.set(null);
    success.set(null);

    try {
      await performSave();
      return true;
    } catch (err) {
      console.error('Settings save before navigation failed:', err);
      error.set(err.message || 'Failed to save settings');
      return false;
    } finally {
      loading.set(false);
    }
  }

  async function performSaveWithToast(toastMessage) {
    await performSave();
    success.set(toastMessage);
    setTimeout(() => success.set(null), 2000);
  }

  function clearJobtreadOrgOnKeyChange() {
    availableOrganizations.set([]);
    formData.update((current) => ({ ...current, jobtreadOrgId: '' }));
  }

  function updateBankAccountName(accountID) {
    const accounts = get(xeroBankAccounts);
    const acc = accounts.find((a) => a.accountID === accountID);
    billSettings.update((current) => ({
      ...current,
      xeroExpenseBankAccountName: acc?.name || ''
    }));
  }

  function disableBillTrackingIfEnabled() {
    billSettings.update((current) => disableTrackingSlots(current));
  }

  function disableInvoiceTrackingIfEnabled() {
    invoiceTrackingSettings.update((current) => {
      const tracking = (current.tracking || []).map((slot) => ({ ...slot, enabled: false }));
      return { ...current, ...legacyFromSlot1(tracking, 'jtJobField'), tracking };
    });
  }

  return {
    loading,
    error,
    success,
    initialized,
    subscriptionData,
    subscriptionLoading,
    subscriptionError,
    subscriptionMessage,
    selectedPlan,
    subscribing,
    formData,
    xeroTaxRates,
    loadingTaxRates,
    managedByOwner,
    xeroManagedByOwner,
    jobtreadManagedByOwner,
    jobtreadConnectionStatus,
    jobtreadOrgName,
    availableOrganizations,
    fetchingOrganizations,
    showManualOrgId,
    autoSyncOnLoginLocal,
    savingAutoSync,
    assignmentLensDefault,
    savingAssignmentLens,
    seeTeamUploads,
    savingSeeTeamUploads,
    billSettings,
    xeroBankAccounts,
    loadingBankAccounts,
    vendorAliasesList,
    loadingVendorAliases,
    vendorAliasSuccess,
    invoiceTrackingSettings,
    invoiceCostFeeSettings,
    trackingCategories,
    loadingTrackingCategories,
    trackingCategoriesError,
    xeroConnected,
    xeroNeedsFix,
    checkingXeroStatus,
    showDisconnectConfirm,
    disconnecting,
    jobtreadConnected,
    formValid,
    isQuickBooks,
    billSettingsValid,
    invoiceTrackingValid,
    initializeSettings,
    resetSettings,
    loadSettings,
    checkXeroConnection,
    checkJobTreadAuthStatus,
    disconnectXero,
    loadSubscriptionStatus,
    handleSubscribe,
    openCustomerPortal,
    handleCountryChange,
    fetchOrganizations,
    checkJobTreadConnection,
    performSave,
    saveSettingsQuietly,
    saveAutoSyncOnLogin,
    saveAssignmentLensDefault,
    saveSeeTeamUploads,
    saveBillSettings,
    saveInvoiceTrackingSettings,
    loadTrackingCategories,
    fetchXeroBankAccounts,
    fetchVendorAliases,
    deleteVendorAlias,
    saveSettings,
    saveSettingsForNavigation,
    performSaveWithToast,
    clearJobtreadOrgOnKeyChange,
    updateBankAccountName,
    disableBillTrackingIfEnabled,
    disableInvoiceTrackingIfEnabled,
    fetchXeroTaxRates
  };
}

export const settingsStore = createSettingsStore();
